<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { LayoutGrid, MessageSquare, AlertTriangle, FileText, Rocket, Pencil, Users, Clock } from 'lucide-svelte';
  import { activityApi } from '$lib/api/activity';
  import { membersApi } from '$lib/api/members';
  import { adminUsersApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { relTime } from '$lib/utils/format';
  import Avatar from '$lib/components/ui/Avatar.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';

  const { projectId }: { projectId: number } = $props();
  const admin = $derived(session.isAdmin);

  const activityQuery = createQuery(() => ({
    queryKey: ['project-activity', projectId],
    queryFn: () => activityApi.forProject(projectId, { per_page: 100 })
  }));

  // Same name-lookup pattern as TicketDetailPage: members endpoint covers
  // project grantees; admin users list additionally covers an admin actor
  // (admins aren't in the members/grants list).
  const namesQuery = createQuery(() => ({
    queryKey: ['project-names', projectId, admin],
    queryFn: async () => {
      const map = new Map<number, string>();
      const members = await membersApi.forProject(projectId);
      members.results.forEach((u) => map.set(u.id, u.name));
      if (admin) {
        const users = await adminUsersApi.list({ per_page: 100 });
        users.results.forEach((u) => map.set(u.id, u.name || u.email));
      }
      return map;
    }
  }));
  function actorName(id: number | null): string {
    if (id === null) return 'System';
    return namesQuery.data?.get(id) ?? `User #${id}`;
  }

  const ICONS: Record<string, any> = {
    task: LayoutGrid,
    ticket: MessageSquare,
    comment: MessageSquare,
    debt: AlertTriangle,
    doc: FileText,
    deploy: Rocket,
    project: Pencil,
    access: Users
  };
  function actIcon(type: string) {
    return ICONS[type] ?? Clock;
  }
</script>

{#if activityQuery.isLoading}
  <p style="color:var(--t3);font-size:13.5px">Loading activity…</p>
{:else if activityQuery.isError}
  <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(activityQuery.error)}</p>
{:else if activityQuery.data?.results.length}
  <div class="card">
    {#each activityQuery.data.results as a, i (a.id)}
      {#if i > 0}<div class="divider"></div>{/if}
      <div class="act-row">
        <div class="act-ic">
          {#if actIcon(a.type)}
            {@const Icon = actIcon(a.type)}
            <Icon size={16} />
          {/if}
        </div>
        <div style="flex:1;min-width:0">
          <div class="act-tx">{a.text}</div>
          <div class="act-meta">{actorName(a.actor_user_id)} · {relTime(a.created_at)}</div>
        </div>
        {#if a.actor_user_id}<Avatar id={a.actor_user_id} name={actorName(a.actor_user_id)} size={26} />{/if}
      </div>
    {/each}
  </div>
{:else}
  <EmptyState
    icon={Clock}
    heading="No activity yet"
    sub="Task moves, triage, deploys, debt and doc changes on this project show up here — scoped to people with access."
  />
{/if}
